import { useMemo } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useFormField, useTabFilter } from '@frontend/design-system';
import { StockImageGalleryPanel } from './stock-image-gallery-panel';
import { LocationIdsFieldProps } from './types';
import { YourImageGalleryPanel } from './your-image-gallery-panel';

type ImageGalleryTab = {
  label: string;
  id: 'yours' | 'stock';
};

type Props = {
  disableSystemGallery?: boolean;
  disableUserGallery?: boolean;
  handleImageSelect: (media?: Media) => void;
  imageSize?: 'small' | 'large';
  locationIds: string[];
  onDoubleClick?: (media?: Media) => void;
  selectedImages: (Media | undefined)[];
  showLocationFilter?: boolean;
  trackingId: string;
};

export const ImageGallery = ({
  disableSystemGallery = false,
  disableUserGallery = false,
  locationIds,
  handleImageSelect,
  imageSize,
  onDoubleClick,
  selectedImages,
  showLocationFilter = false,
  trackingId,
  ...rest
}: Props) => {
  const { t } = useTranslation('messages');
  const { activeTab, getTabProps, filterProps, Tab, TabFilter } = useTabFilter('yours');

  const tabs = useMemo(() => {
    const tabsList: ImageGalleryTab[] = [];
    if (!disableUserGallery) {
      tabsList.push({ label: t('Your Images'), id: 'yours' });
    }
    if (!disableSystemGallery) {
      tabsList.push({ label: t('Find Stock Images'), id: 'stock' });
    }
    return tabsList;
  }, [disableUserGallery, disableSystemGallery, t]);

  const locationIdsFieldProps = useFormField({ type: 'checklist', value: locationIds }, [
    [...locationIds].sort((a, b) => a.localeCompare(b)).join(','),
  ]);

  const sharedProps = useMemo(
    () => ({
      handleImageSelect,
      imageSize,
      locationIds,
      locationIdsFieldProps: { ...locationIdsFieldProps, name: 'locationIds' } as LocationIdsFieldProps,
      onDoubleClick,
      selectedImages,
      trackingId,
    }),
    [handleImageSelect, imageSize, locationIds, locationIdsFieldProps, onDoubleClick, selectedImages, trackingId]
  );

  return (
    <div {...rest}>
      <TabFilter
        {...filterProps}
        css={{
          height: theme.spacing(5),
          minHeight: 'unset',
          button: {
            flexBasis: 'unset',
            minWidth: 'unset',
          },
          '&:after': {
            display: 'none',
          },
        }}
      >
        {tabs.map(({ label, id }, index) => (
          <Tab
            key={id}
            {...getTabProps(id)}
            css={{
              background: theme.colors.white,
              borderBottom: activeTab === id ? `1px solid ${theme.colors.primary50}` : 'none',
              borderTopLeftRadius: index === 0 ? theme.borderRadius.medium : 0,
              borderTopRightRadius: index === tabs.length - 1 ? theme.borderRadius.medium : 0,
              padding: theme.spacing(1, 2),
              '&[aria-selected="true"]': {
                color: theme.colors.neutral90,
              },
              '&:focus, &:hover': {
                background: theme.colors.white,
              },
            }}
            trackingId={`${trackingId}-${id}-image-tab`}
          >
            {label}
          </Tab>
        ))}
      </TabFilter>
      {activeTab === 'yours' && <YourImageGalleryPanel {...sharedProps} showLocationFilter={showLocationFilter} />}
      {activeTab === 'stock' && <StockImageGalleryPanel {...sharedProps} />}
    </div>
  );
};
